// The On this PC card (decision 064, F420; decision 124): what this PC
// holds of an airframe and what the pilot can do with it here: book the
// aircraft, start the watch again, cancel the booking, watch the flight of
// an aircraft someone else has booked, share it, the maintenance jobs (the
// livery is offered from the airframe's settings, F487). The upload happens
// when X-Plane is closed parked, never from a button. Rendered under the
// host alone; the browser shows the page without it.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";

import { queries } from "@/api";
import { useHostEvents } from "@/host/events";
import { bookedLine, checkoutLine, formatBytes, inProgressLine, leaseLine, localClock, transferLine } from "@/host/format";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import { OpenSlotLine } from "@/components/ScheduleBoard";
import { ShareDialog } from "@/components/ShareAirframe";
import type { PublishInput, StatusRow, TransferMessage } from "@/host/types";
import { MAINTENANCE, maintenanceLabel } from "@/lib/format";
import { utcClock } from "@/lib/schedule";

/** The rows in the order a pilot looks for them (Gianluca, 2026-10-27: "just to have more order"): the aircraft shared with you, yours on the line, the ones only on this PC, the rest; within a group the one you have first. */
export function groups(rows: StatusRow[]): { id: string; title: string; rows: StatusRow[] }[] {
  const by = (c: StatusRow["classification"]) => rows.filter((r) => r.classification === c).sort((a, b) => Number(!!b.local?.lease) - Number(!!a.local?.lease) || a.reg.localeCompare(b.reg));
  return [
    // The platform unreachable (decision 139): the aircraft are here, the platform's word on them is not.
    { id: "offline", title: "ShariFly cannot be reached right now", rows: by("offline") },
    { id: "shared-with-me", title: "Shared with you", rows: by("shared_with_me") },
    { id: "shared-by-me", title: "Shared by you", rows: by("shared_by_me") },
    { id: "personal", title: "Not shared yet", rows: by("personal") },
    { id: "unknown", title: "No longer shared with you", rows: by("unknown") },
  ].filter((g) => g.rows.length > 0);
}

/** The row of this PC for a platform airframe, or null when this PC knows nothing of it. */
export function rowFor(rows: StatusRow[] | undefined, airframeId: string): StatusRow | null {
  return rows?.find((r) => r.platform?.id === airframeId || r.local?.platform_id === airframeId) ?? null;
}

type Note = { ok: boolean; text: string };


/** The one question before a cancellation (F486): nothing is written. */
export function cancelQuestion(reg: string, watch: string | null): string {
  return `Cancel the booking of ${reg}? Nothing is written: the aircraft stays on ShariFly as the last entry left it, and it is available to the others again.${watch ?? ""}`;
}

/** " The watch stops first." for this aircraft's session, " The watch of N75TJ stops first." for another's, nothing without one. */
export function watchNote(flying: boolean, sessionReg: string | null): string | null {
  if (flying) return " The watch stops first.";
  if (sessionReg) return ` The watch of ${sessionReg} stops first.`;
  return null;
}


/** The actions of the card, shared by the cards on a page: one set of mutations, the notes per airframe. */
export function useLocalActions() {
  const host = useHost()!;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [setLastUsed, setSetLastUsed] = useState(true);
  const [notes, setNotes] = useState<Record<string, Note>>({});
  // The airframe being shared, its dialog over the page (the card moves to
  // another group once the airframe is shared, and would lose the dialog).
  const [sharing, setSharing] = useState<StatusRow | null>(null);
  const note = (uuid: string, ok: boolean, text: string) => setNotes((n) => ({ ...n, [uuid]: { ok, text } }));
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["host"] });
    queryClient.invalidateQueries({ queryKey: ["airframe"] });
    queryClient.invalidateQueries({ queryKey: ["airframes"] });
  };
  // Book the aircraft starts the watch in the same click (decision 065):
  // the files installed, then the session that uploads the aircraft when
  // X-Plane is closed; a watch that cannot start is said beside the booking.
  const checkout = useMutation({
    mutationFn: async (uuid: string) => {
      const outcome = await host.checkout(uuid, setLastUsed);
      let watch: string | null = null;
      try {
        await host.flyStart(uuid);
      } catch (e) {
        watch = String(e);
      }
      return { outcome, watch };
    },
    onSuccess: ({ outcome, watch }, uuid) => {
      note(uuid, watch === null, watch === null ? checkoutLine(outcome) : `${bookedLine(outcome)}. The watch did not start: ${watch}. Start it again from the airframe's page, or ShariFly will not upload the aircraft when you close X-Plane.`);
      refresh();
      // The pre-flight is the next step (F507, decision 124): every Book lands there, from the fleet or the airframe's page.
      navigate({ to: "/airframes/$id/preflight", params: { id: outcome.lease.airframe_id } });
    },
    onError: (e, uuid) => {
      note(uuid, false, String(e));
      refresh();
    },
  });
  // A cancelled booking while the session waits for X-Plane (the watch
  // starts with the booking, decision 065): the watch is stopped first.
  const release = useMutation({
    mutationFn: async ({ uuid, stopFirst }: { uuid: string; stopFirst: boolean }) => {
      if (stopFirst) await host.flyStop();
      return host.release(uuid);
    },
    onSuccess: (lease, { uuid }) => {
      note(uuid, true, lease.state === "released" ? "Booking cancelled: the aircraft is available again" : `The aircraft is ${lease.state}`);
      refresh();
    },
    onError: (e, { uuid }) => note(uuid, false, String(e)),
  });
  const fly = useMutation({
    mutationFn: (uuid: string) => host.flyStart(uuid),
    onSuccess: refresh,
    onError: (e, uuid) => note(uuid, false, String(e)),
  });
  const watch = useMutation({
    mutationFn: (uuid: string) => host.watchStart(uuid),
    onSuccess: refresh,
    onError: (e, uuid) => note(uuid, false, String(e)),
  });
  const maintain = useMutation({
    mutationFn: ({ uuid, action }: { uuid: string; action: string }) => host.maintain(uuid, action),
    onSuccess: (o, { uuid, action }) => {
      const label = maintenanceLabel(action);
      const text = o.queued
        ? `${label[0].toUpperCase()}${label.slice(1)} requested; the session starts it and shows it in the cockpit`
        : o.condition?.in_progress
          ? `${label[0].toUpperCase()}${label.slice(1)} started, done at ${new Date(o.condition.in_progress.completes_at).toLocaleTimeString()}; the aircraft is not to be flown meanwhile`
          : `${label[0].toUpperCase()}${label.slice(1)} done`;
      note(uuid, true, text);
      refresh();
    },
    onError: (e, { uuid }) => note(uuid, false, String(e)),
  });
  const publish = useMutation({
    mutationFn: ({ uuid, input }: { uuid: string; input: PublishInput }) => host.publish(uuid, input),
    onSuccess: (o, { uuid }) => {
      note(uuid, true, `${o.reg}${o.name ? ` (${o.name})` : ""} is shared${o.files ? ` with its files (${o.files.files} files, ${formatBytes(o.files.bytes)})` : ""}.`);
      refresh();
    },
    onError: (e, { uuid }) => note(uuid, false, String(e)),
  });
  const busy = checkout.isPending || release.isPending || fly.isPending || watch.isPending || publish.isPending || maintain.isPending;
  return { host, setLastUsed, setSetLastUsed, notes, note, busy, checkout, release, fly, watch, maintain, publish, sharing, setSharing };
}

export type LocalActions = ReturnType<typeof useLocalActions>;

/** The card for one airframe of this PC. `actions` come from `useLocalActions` of the page (one set for all its cards). */
export function OnThisPc({ row, actions, meId, signedIn, compact = false }: { row: StatusRow; actions: LocalActions; meId: string | null; signedIn: boolean; compact?: boolean }) {
  const info = useQuery(hostQueries.info(actions.host));
  const events = useHostEvents();
  const session = info.data?.session ?? null;
  const readOnly = info.data?.read_only ?? false;
  return (
    <Card
      row={row}
      signedIn={signedIn}
      meId={meId}
      readOnly={readOnly}
      flying={session?.hs_uuid === row.uuid}
      sessionElsewhere={!!session && session.hs_uuid !== row.uuid}
      sessionReg={session && session.hs_uuid !== row.uuid ? session.reg : null}
      xplaneOpen={info.data?.xplane_open === true}
      busy={actions.busy}
      note={actions.notes[row.uuid]}
      compact={compact}
      onCheckout={() => actions.checkout.mutate(row.uuid)}
      // The watch running on this PC stops first, this aircraft's or another's (F486): one session at a time.
      onRelease={() => actions.release.mutate({ uuid: row.uuid, stopFirst: !!session })}
      onFly={() => actions.fly.mutate(row.uuid)}
      onWatch={() => actions.watch.mutate(row.uuid)}
      onShareOpen={() => actions.setSharing(row)}
      onMaintain={(action) => actions.maintain.mutate({ uuid: row.uuid, action })}
      transfer={events.transfers[row.uuid] ?? null}
    />
  );
}

type CardProps = {
  row: StatusRow;
  signedIn: boolean;
  meId: string | null;
  readOnly: boolean;
  flying: boolean;
  sessionElsewhere: boolean;
  /** The registration of the session running on this PC for another airframe (F486), when any. */
  sessionReg: string | null;
  /** X-Plane runs on this PC (2026-11-03): the files cannot be put in place, so Book the aircraft waits. */
  xplaneOpen: boolean;
  busy: boolean;
  note?: Note;
  /** In a table row: the state and the actions alone, no title. */
  compact: boolean;
  onCheckout: () => void;
  onRelease: () => void;
  onFly: () => void;
  onWatch: () => void;
  /** Opens the share dialog over the page for this airframe. */
  onShareOpen: () => void;
  onMaintain: (action: string) => void;
  transfer: TransferMessage | null;
};

function Card({ row, signedIn, meId, readOnly, flying, sessionElsewhere, sessionReg, xplaneOpen, busy, note, compact, onCheckout, onRelease, onFly, onWatch, onShareOpen, onMaintain, transfer }: CardProps) {
  // A cancellation asks one question that says what happens (F486,
  // decision 102); the watch running on this PC stops by itself after it.
  const [asking, setAsking] = useState<"cancel" | null>(null);
  // The question goes with the session: ended by itself, a fresh booking never meets it.
  useEffect(() => {
    setAsking(null);
  }, [flying]);
  const lease = leaseLine(row);
  // The maintenance job under way (F406): the aircraft is being worked on,
  // not flown. The local copy may be older than the job's end: past it, the
  // job counts as done here (the platform settles it at the next request).
  const pending = row.local?.condition?.in_progress ?? null;
  const inProgress = pending && new Date(pending.completes_at).getTime() > Date.now() ? pending : null;
  const heldHere = !!row.local?.lease;
  // The platform's pilot (F413): another pilot flying it now.
  const other = row.platform?.lease && row.platform.lease.holder.id !== meId ? row.platform.lease : null;
  const published = row.platform !== null;
  // Off ShariFly by its owner (decision 138): nothing is booked until it is back.
  const unlisted = !!row.platform?.unlisted_at;
  const personal = row.classification === "personal";
  const member = row.classification === "shared_by_me" || row.classification === "shared_with_me";
  const canAct = signedIn && published && member && !busy && !sessionElsewhere;
  // A return or a cancellation is never greyed by a session on this PC: the question says which watch stops.
  const canLeave = signedIn && published && member && !busy;
  const canPublish = signedIn && !published && personal && !busy;
  const Tag = compact ? "div" : "section";
  return (
    <Tag className={compact ? "flex flex-col gap-1" : "card"} aria-label={compact ? undefined : `${row.reg} on this PC`} data-testid={`on-this-pc-${row.reg}`}>
      {!compact ? (
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-[1.0714rem] font-semibold">{row.reg}</h2>
          {row.platform?.name ? <span className="muted">{row.platform.name}</span> : null}
          <span className="kicker">on this PC</span>
          {!row.is_parked ? <span className="pill pill-warn">not parked</span> : null}
          {row.pending_outbox > 0 ? <span className="pill pill-warn">{row.pending_outbox} pending returns</span> : null}
          {row.local_conflicts > 0 ? <span className="pill pill-bad">{row.local_conflicts} waiting for the owner</span> : null}
          {flying ? <span className="pill pill-ok">session running</span> : null}
        </div>
      ) : null}
      {!compact ? (
        <div className="muted mt-1 text-xs tnum">
          MSN {row.msn}, {row.livery}, {row.cl650_version ?? "version unknown"}
          {row.on_machine ? `, ${row.flights} flights in the log` : ""}
        </div>
      ) : null}
      <div className={`text-xs muted ${compact ? "" : "mt-1"}`}>
        {other
          ? `booked by ${(other.holder.name || other.holder.handle)} until ${localClock(other.expires_at)}${row.on_machine ? "" : "; not on this PC yet"}`
          : !row.on_machine
            ? "not on this PC yet: Book the aircraft installs it beside your other airframes, X-Plane closed"
            : (lease ?? (published ? "available" : personal ? "not on ShariFly yet: only on this PC" : row.classification === "offline" ? "on this PC; nothing is booked or returned until ShariFly answers again" : "no longer shared with you (hidden, or you are no longer a pilot of it)"))}
        {flying ? "; start X-Plane and fly, ShariFly uploads the aircraft when you close it parked" : ""}
        {!compact && row.local?.installed ? `; entry ${row.local.installed.seq} installed` : ""}
        {!compact && row.local?.last_checkin_seq ? `; last returned as entry ${row.local.last_checkin_seq}` : ""}
        {row.error ? `; ${row.error}` : ""}
      </div>
      {!compact && row.platform?.slot && row.platform.slot.yours && row.platform.slot.state === "open" ? <OpenSlotLine slot={row.platform.slot} reg={row.reg} /> : null}
      {!compact && heldHere && row.platform?.slot && !row.platform.slot.yours && (row.platform.slot.state === "planned" || row.platform.slot.state === "open") ? (
        <div className="alert mt-2 text-xs" data-testid="next-slot-line">
          Next slot {utcClock(new Date(row.platform.slot.starts_at))} UTC for {row.platform.slot.pilot.name || row.platform.slot.pilot.handle}: be parked and out of X-Plane by then, or their slot waits and lapses.
        </div>
      ) : null}
      <div className={`flex flex-wrap gap-2 ${compact ? "mt-1" : "mt-2"}`}>
        {!published ? (
          personal ? (
            <button className={`btn btn-primary${compact ? " btn-sm" : ""}`} disabled={!canPublish} onClick={onShareOpen} aria-label={`Share ${row.reg}`} title={signedIn ? "Put this airframe on ShariFly and get a link for your friends" : "Sign in first"}>
              Share this airframe
            </button>
          ) : null
        ) : !lease ? (
          <>
            {compact && other ? null : (
            <button className={`btn btn-primary${compact ? " btn-sm" : ""}`} disabled={!canAct || !!other || xplaneOpen || unlisted} onClick={onCheckout} aria-label={`Book ${row.reg}`} title={unlisted ? "Unlisted: put it back on ShariFly from its settings to book it" : other ? `${(other.holder.name || other.holder.handle)} has the aircraft; watch their flight, or wait until they are done` : xplaneOpen ? "X-Plane is open: close it to book the aircraft. The files are put in place only while X-Plane is closed." : undefined}>
              Book the aircraft
            </button>
            )}
            {row.on_machine && other ? (
              <button className={`btn${compact ? " btn-sm" : ""}${compact ? " btn-primary" : ""}`} disabled={!canAct || readOnly} onClick={onWatch} aria-label={`Watch the flight of ${row.reg}`} title="Their state installed here read-only, their dirt live in your cockpit as they fly; nothing to book or return">
                Watch the flight
              </button>
            ) : null}
          </>
        ) : (
          <>
            {/* Booked without the files here (decision 060): nothing to fly or upload, only to cancel.
                The watch that uploads the aircraft starts with the booking; a watch that stopped is started again. */}
            {!flying && row.on_machine && !compact ? (
              <button className="btn" disabled={!canAct || !!inProgress} onClick={onFly} aria-label={`Start watching X-Plane for ${row.reg}`} title={inProgress ? "The aircraft is being worked on; fly when the job is done" : "The watch uploads the aircraft when you close X-Plane parked; it started with the booking and is started again here"}>
                Start the watch again
              </button>
            ) : null}
            {!compact || !row.on_machine ? (
              <button className={`btn btn-danger${compact ? " btn-sm" : ""}`} disabled={!canLeave} onClick={() => setAsking("cancel")} aria-label={`Cancel the booking of ${row.reg}`} title="Nothing is written; the watch stops with the booking">
                Cancel the booking
              </button>
            ) : null}
          </>
        )}
      </div>
      {asking !== null ? (
        <div className="alert alert-warn mt-2 flex flex-wrap items-center gap-2 text-sm" role="alertdialog" aria-label="Cancelling the booking" data-testid="leaving">
          <span className="flex-1">{cancelQuestion(row.reg, watchNote(flying, sessionReg))}</span>
          <button type="button" className="btn btn-sm" onClick={() => setAsking(null)} data-testid="leaving-stay">
            {flying ? "Keep flying" : "Keep the booking"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => {
              setAsking(null);
              onRelease();
            }}
            data-testid="leaving-go"
          >
            Yes, cancel the booking
          </button>
        </div>
      ) : null}
      {transfer ? <TransferBar transfer={transfer} /> : null}
      {!compact && heldHere ? (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`maintenance-${row.reg}`}>
          <span className="kicker">Maintenance</span>
          {inProgress ? (
            <span className="pill pill-warn">{inProgressLine(inProgress)}</span>
          ) : (
            MAINTENANCE.map((m) => (
              <button key={m.action} type="button" className="btn btn-sm" disabled={!signedIn || busy || sessionElsewhere} onClick={() => onMaintain(m.action)} aria-label={`${m.label} ${row.reg}`} title={m.hint}>
                {m.label}, {m.duration}
              </button>
            ))
          )}
        </div>
      ) : null}
      {note ? <p className={`mt-2 text-xs ${note.ok ? "notice" : "alert"}`}>{note.text}</p> : null}
    </Tag>
  );
}

/** The share dialog over the page, for the airframe a card opened it for; rendered once per page. */
export function ShareOverlay({ actions }: { actions: LocalActions }) {
  const row = actions.sharing;
  if (!row) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-auto bg-ink/40 p-6" role="dialog" aria-label={`Share ${row.reg}`} data-testid="share-overlay">
      <div className="w-full max-w-xl">
        <ShareDialog row={row} onShare={(input) => actions.publish.mutateAsync({ uuid: row.uuid, input })} onClose={() => actions.setSharing(null)} />
      </div>
    </div>
  );
}

/** A transfer under way (F407): what moves, which way, how far. */
export function TransferBar({ transfer }: { transfer: TransferMessage }) {
  const pct = transfer.total > 0 ? Math.min(100, Math.round((transfer.done / transfer.total) * 100)) : 0;
  return (
    <div className="mt-2 flex flex-col gap-1" data-testid={`transfer-${transfer.hs_uuid}`}>
      <span className="muted text-xs">
        {transferLine(transfer)} ({pct}%)
      </span>
      <progress className="transfer" max={100} value={pct} aria-label={transferLine(transfer)} />
    </div>
  );
}

/** The On this PC card of an airframe's page (decision 064): under the host alone, for an airframe this PC knows. */
export function AirframeOnThisPc({ airframeId }: { airframeId: string }) {
  const host = useHost();
  if (!host) return null;
  return <AirframeOnThisPcUnderHost airframeId={airframeId} />;
}

function AirframeOnThisPcUnderHost({ airframeId }: { airframeId: string }) {
  const host = useHost()!;
  const status = useQuery(hostQueries.status(host));
  const me = useQuery(queries.me());
  const actions = useLocalActions();
  const row = rowFor(status.data, airframeId);
  if (!row) return null;
  return (
    <div className="space-y-2">
      <OnThisPc row={row} actions={actions} meId={me.data?.user.id ?? null} signedIn={!!me.data} />
      {row.platform ? <ReturnedFromThisPc hsUuid={row.uuid} /> : null}
      <ShareOverlay actions={actions} />
    </div>
  );
}

/** What this PC returned of an airframe (PRD 02 RF-60): every entry it made, each file and its size; folded. */
function ReturnedFromThisPc({ hsUuid }: { hsUuid: string }) {
  const host = useHost()!;
  const uploads = useQuery({ queryKey: ["host", "uploads", hsUuid], queryFn: () => host.uploads(hsUuid) });
  const list = uploads.data ?? [];
  return (
    <details className="card !py-2.5" data-testid="returned-from-this-pc">
      <summary className="cursor-pointer text-sm font-semibold">Returned from this PC ({list.length})</summary>
      {list.length === 0 ? <p className="muted mt-1 text-xs">Nothing returned from here yet.</p> : null}
      {list.map((u) => (
        <details key={u.version.id} className="mt-2 text-xs">
          <summary className="cursor-pointer">
            Entry {u.version.seq}: {u.metadata_only ? "numbers only, no files" : `${u.files.length} files, ${formatBytes(u.package_bytes)}`}, {u.flights_added} new flights, {u.recorded_at}
          </summary>
          {u.files.length > 0 ? (
            <table className="table mono mt-2">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Bytes</th>
                  <th>SHA-256</th>
                </tr>
              </thead>
              <tbody>
                {u.files.map((f) => (
                  <tr key={f.path}>
                    <td>{f.path.replace(/^airframe\/[0-9a-f]+\//, "")}</td>
                    <td className="tnum">{f.bytes}</td>
                    <td>{f.sha256.slice(0, 12)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </details>
      ))}
    </details>
  );
}
